import type { IReservationTableRef } from '@/interfaces/IReservation';
import type { IReservationTableGroup } from '@/interfaces/IReservationSettings';
import { isOnlineReservationTable } from './reservation-tables';

export const GROUP_MIN_TABLES = 2;
export const GROUP_MAX_TABLES = 6;
export const GROUP_PARTY_MAX = 200;
export const GROUP_NAME_MAX = 64;

export interface IReservationPartyRange {
  minParty: number;
  maxParty: number;
}

export type TGroupProblem = 'offline' | 'belowMinimum' | 'overSeats';

/** The tables behind the ids, in id order; unknown ids are ignored. */
function membersOf(
  tableIds: readonly number[],
  tables: readonly IReservationTableRef[]
): IReservationTableRef[] {
  const byId = new Map(tables.map((table) => [table.id, table]));
  const members: IReservationTableRef[] = [];
  for (const id of tableIds) {
    const table = byId.get(id);
    if (table) members.push(table);
  }
  return members;
}

function seatsOf(members: readonly IReservationTableRef[]): number {
  return members.reduce((total, table) => total + table.capacity, 0);
}

export function groupSeatsTotal(
  tableIds: readonly number[],
  tables: readonly IReservationTableRef[]
): number {
  return seatsOf(membersOf(tableIds, tables));
}

/** D6: up to the summed seats (capped at 200), from one more than the largest member. */
export function defaultGroupRange(
  tableIds: readonly number[],
  tables: readonly IReservationTableRef[]
): IReservationPartyRange {
  const members = membersOf(tableIds, tables);
  const largest = Math.max(0, ...members.map((table) => table.capacity));
  const maxParty = Math.min(seatsOf(members), GROUP_PARTY_MAX);
  return { minParty: Math.min(largest + 1, maxParty), maxParty };
}

/** Member names joined by " + "; when that is too long, "{first} +{n}". */
export function groupNameFor(
  tableIds: readonly number[],
  tables: readonly IReservationTableRef[]
): string {
  const names = membersOf(tableIds, tables).map((table) => table.name);
  const joined = names.join(' + ');
  if (joined.length <= GROUP_NAME_MAX) return joined;
  if (names.length === 1) return names[0].slice(0, GROUP_NAME_MAX);
  return `${names[0]} +${names.length - 1}`;
}

/** Why a saved combination cannot be offered as stored, with its current seats. */
export function groupProblem(
  group: IReservationTableGroup,
  tables: readonly IReservationTableRef[]
): { kind: TGroupProblem | null; seats: number } {
  const members = membersOf(group.tableIds, tables);
  const seats = seatsOf(members);
  if (
    members.length !== group.tableIds.length ||
    members.some((table) => !isOnlineReservationTable(table))
  )
    return { kind: 'offline', seats };
  if (seats < group.minParty) return { kind: 'belowMinimum', seats };
  if (seats < group.maxParty) return { kind: 'overSeats', seats };
  return { kind: null, seats };
}

/** The ids that can be joined: enabled, online and with seats. */
export function joinableTableIds(
  ids: readonly number[],
  tables: readonly IReservationTableRef[]
): number[] {
  return membersOf(ids, tables)
    .filter((table) => isOnlineReservationTable(table) && table.capacity > 0)
    .map((table) => table.id);
}

/** The online maximum to raise to for a new combination, or null when it already fits. */
export function raisedMaxParty(
  current: number,
  groupMax: number
): number | null {
  return groupMax > current ? Math.min(groupMax, GROUP_PARTY_MAX) : null;
}
